import React, { useState, useEffect } from 'react';
import { FaYoutube } from 'react-icons/fa';
import fetchVideos from '../../services/fetchVideos';
import VideoCard from './VideoCard';

const Syntube = () => {
  const [videos, setVideos] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    const loadVideos = async () => {
      try {
        setLoading(true);
        const data = await fetchVideos();
        setVideos(data.items);
        setTotalPages(Math.ceil(data.items.length / itemsPerPage));
        setError(null);
      } catch (err) {
        setError('Failed to load videos. Please try again later.');
        console.error('Error loading videos:', err);
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleVideoSelect = (index) => {
    setCurrentVideoIndex(index);
  };

  // Calculate the videos to show for the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentVideos = videos.slice(startIndex, endIndex);

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mt-4 font-header">SYNTUBE</h1>
        <p className="text-gray-500 mt-2 font-body">
          SYNTAX offers free video tutorials and lectures for general engineering courses to the GCOE community.
        </p>
        <hr className="border-gray-300 mt-6 w-1/3 mx-auto" />
      </div>

      {/* Syntube Section */}
      <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0 mb-6">
        <div className="flex items-center space-x-4">
          <div className="bg-black p-4 rounded-full">
            <FaYoutube size={30} color="#FFF" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 font-header">SYNTUBE</h2>
        </div>

        {/* View More Videos Button */}
        <div className="flex space-x-2 justify-center">
          <a className="inline-flex text-center bg-blue-800 text-white px-4 py-2 rounded-md hover:bg-blue-900 font-body" target="_blank" href="https://www.youtube.com/@dlsu.syntax">
            View More Videos
          </a>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="text-center font-body">
          Loading videos...
        </div>
      )}
      
      {error && (
        <div className="text-center text-red-600 font-body">
          {error}
        </div>
      )}

      {/* Video Player and List */}
      {!loading && !error && videos.length > 0 && (
        <div className="flex flex-col lg:flex-row gap-6 h-full mb-4">
          {/* Main Video Player */}
          <div className="lg:w-2/3 h-full">
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${videos[currentVideoIndex]?.contentDetails?.videoId}`}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
              ></iframe>
            </div>
          </div>

          {/* Video List and Pagination */}
          <div className="lg:w-1/3 flex flex-col h-full">
            {/* Video List */}
            <div className="flex-1 overflow-y-auto space-y-4" style={{ height: 'calc(56.25vw * 0.66)' }}>
              {currentVideos.map((video, index) => (
                <VideoCard
                  key={video.contentDetails.videoId}
                  video={video}
                  isSelected={currentVideoIndex === startIndex + index}
                  onClick={() => handleVideoSelect(startIndex + index)}
                />
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4 mt-4">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-md font-body ${
                    currentPage === 1
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-700 hover:bg-blue-800 text-white'
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex space-x-2">
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`px-4 py-2 rounded-md font-body ${
                        currentPage === index + 1
                          ? 'bg-blue-800 text-white'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-md font-body ${
                    currentPage === totalPages
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-700 hover:bg-blue-800 text-white'
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Syntube; 